import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, User, LogOut, Link as LinkIcon } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import ActiveTimerWarningModal from './ActiveTimerWarningModal';

interface BusinessProfileDropdownProps {
  className?: string;
}

export default function BusinessProfileDropdown({ className = '' }: BusinessProfileDropdownProps) {
  const { selectedBusiness, role, loading } = useBusiness();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [currentResource, setCurrentResource] = useState<any>(null);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Active timer warning modal state
  const [isActiveTimerWarningOpen, setIsActiveTimerWarningOpen] = useState(false);
  const [activeTimerBookingTitle, setActiveTimerBookingTitle] = useState<string>('');

  // Fetch current resource info (only for non-admin users)
  useEffect(() => {
    const fetchCurrentResource = async () => {
      if (!selectedBusiness?.id || !user?.id || role === 'admin') {
        setCurrentResource(null);
        return;
      }

      try {
        const { data: resource, error } = await supabase
          .from('resources')
          .select('*')
          .eq('business_id', selectedBusiness.id)
          .eq('auth_user_id', user.id)
          .single();

        if (error) {
          console.error('Error fetching current resource:', error);
          return;
        }

        setCurrentResource(resource);
      } catch (error) {
        console.error('Error fetching current resource:', error);
      }
    };

    fetchCurrentResource();
  }, [selectedBusiness?.id, user?.id, role]);

  // Update button position when opening dropdown
  const updateButtonPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonRect(rect);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleLogout = async () => {
    // Check for active timers before allowing logout (only for non-admin users)
    if (!role || role !== 'admin') {
      try {
        const { data: activeTimerLogs, error: timerError } = await supabase
          .from('booking_timer_logs')
          .select(`
            booking_id,
            bookings!inner(title)
          `)
          .eq('resource_id', currentResource?.id)
          .eq('is_running', true)
          .limit(1);

        if (timerError) {
          console.error('Error checking for active timers:', timerError);
        } else if (activeTimerLogs && activeTimerLogs.length > 0) {
          // Active timer found, show warning modal
          const bookingTitle = activeTimerLogs[0].bookings?.title || 'Unknown Booking';
          setActiveTimerBookingTitle(bookingTitle);
          setIsActiveTimerWarningOpen(true);
          setIsOpen(false); // Close the dropdown
          return; // Don't proceed with logout
        }
      } catch (error) {
        console.error('Error checking for active timers:', error);
      }
    }

    // Proceed with logout if no active timers or user is admin
    try {
      await supabase.auth.signOut();
      setIsOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!selectedBusiness) return null;

  // Check if user is a business owner (has owned_business_ids in metadata)
  const userIsBusinessOwner = user?.user_metadata?.owned_business_ids;
  const isAdmin = userIsBusinessOwner || role === 'admin';
  

  return (
    <div className={`relative ${className}`}>
      {/* Profile Icon Button */}
      <button
        ref={buttonRef}
        onClick={() => {
          if (!isOpen) {
            updateButtonPosition();
          }
          setIsOpen(!isOpen);
        }}
        className="flex items-center justify-center w-8 h-8 rounded-full overflow-hidden hover:ring-2 hover:ring-white/20 transition-all duration-200"
        title={isAdmin ? selectedBusiness.name : `${currentResource?.first_name} ${currentResource?.last_name}`}
      >
        {isAdmin ? (
          // Business Logo or Building Icon
          selectedBusiness.booking_settings?.logo_url ? (
            <img 
              src={selectedBusiness.booking_settings.logo_url} 
              alt={`${selectedBusiness.name} logo`}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex items-center justify-center w-8 h-8 bg-gray-600 rounded-full">
              <Building2 className="h-4 w-4 text-white" />
            </div>
          )
        ) : (
          // Resource Profile Photo or Initials
          currentResource?.avatar_url ? (
            <img 
              src={currentResource.avatar_url} 
              alt={`${currentResource.first_name} ${currentResource.last_name}`}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex items-center justify-center w-8 h-8 bg-gray-600 rounded-full text-xs font-medium text-white">
              {currentResource?.first_name?.[0]}{currentResource?.last_name?.[0]}
            </div>
          )
        )}
      </button>

      {/* Dropdown Menu - Rendered via Portal */}
      {isOpen && buttonRect && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-[9999]"
          style={{
            top: buttonRect.bottom + 8,
            right: window.innerWidth - buttonRect.right,
          }}
        >
          {/* Profile Header */}
          <div className="px-4 py-3 border-b border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full overflow-hidden">
                {isAdmin ? (
                  selectedBusiness.booking_settings?.logo_url ? (
                    <img 
                      src={selectedBusiness.booking_settings.logo_url} 
                      alt={`${selectedBusiness.name} logo`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-10 h-10 bg-gray-600 rounded-full">
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                  )
                ) : (
                  currentResource?.avatar_url ? (
                    <img 
                      src={currentResource.avatar_url} 
                      alt={`${currentResource.first_name} ${currentResource.last_name}`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-10 h-10 bg-gray-600 rounded-full text-sm font-medium text-white">
                      {currentResource?.first_name?.[0]}{currentResource?.last_name?.[0]}
                    </div>
                  )
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white truncate">
                  {isAdmin ? selectedBusiness.name : `${currentResource?.first_name} ${currentResource?.last_name}`}
                </div>
                {!isAdmin && (
                  <div className="text-xs text-gray-400 truncate">
                    {currentResource?.roleName || currentResource?.role || 'Team Member'}
                  </div>
                )}
                {isAdmin && selectedBusiness.address && (
                  <div className="text-xs text-gray-400 truncate">
                    {selectedBusiness.address}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {!isAdmin && (
              <button
                onClick={() => {
                  navigate('/profile');
                  setIsOpen(false);
                }}
                className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
              >
                <User className="h-4 w-4 mr-3" />
                Profile
              </button>
            )}
            
            {isAdmin && (
              <button
                onClick={() => {
                  setIsOpen(false);
                }}
                className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
              >
                <LinkIcon className="h-4 w-4 mr-3" />
                Connect Account
              </button>
            )}
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
            >
              <LogOut className="h-4 w-4 mr-3" />
              Log out
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Active Timer Warning Modal */}
      <ActiveTimerWarningModal
        isOpen={isActiveTimerWarningOpen}
        onClose={() => setIsActiveTimerWarningOpen(false)}
        bookingTitle={activeTimerBookingTitle}
      />
    </div>
  );
}
